import styled from '@emotion/styled';
import type { Borrower } from '@racemap/sdk/schema/trackers';
import { USER_ROLE } from '@racemap/utilities/consts/trackers';
import { getUserRoleForTracker } from '@racemap/utilities/functions/trackers';
import { Card } from 'antd';
import type { FC } from 'react';
import { useCurrentUser, useInspectedTracker } from '../../../lib/customHooks';
import { useStore } from '../../../store/reducers';
import { AddUserToTrackers } from '../AddUserToTrackers';
import { TrackerUserRoleTable } from './TrackerUserRoleTable';

export const UserTab: FC = () => {
  const inspectedTracker = useInspectedTracker();
  const currentUser = useCurrentUser();
  const { removeUserFromTracker } = useStore((s) => s.trackers.actions);

  if (inspectedTracker == null || currentUser == null) return <></>;

  const isAdmin = !!currentUser.admin;
  const userRole = getUserRoleForTracker(inspectedTracker, currentUser.id);
  const borrowerObject =
    userRole === USER_ROLE.BORROWER
      ? inspectedTracker.borrowers.find((b) => b.id.toHexString() === currentUser.id)
      : undefined;

  return (
    <Container>
      <CurrentUserRole userRole={userRole} borrowerObject={borrowerObject} isAdmin={isAdmin} />

      <TrackerUserRoleTable
        tracker={inspectedTracker}
        currentUser={currentUser}
        onUserRemove={(userId, userRole, dates) =>
          removeUserFromTracker(inspectedTracker.id, userId, userRole, dates)
        }
      />
      {(isAdmin || (userRole != null && userRole !== USER_ROLE.BORROWER)) && (
        <Card title="Add User">
          <AddUserToTrackers trackerIds={[inspectedTracker.id]} />
        </Card>
      )}
    </Container>
  );
};

const CurrentUserRole: FC<{
  userRole: USER_ROLE | null;
  borrowerObject?: Borrower;
  isAdmin: boolean;
}> = ({ userRole, borrowerObject, isAdmin }) => {
  switch (userRole) {
    case USER_ROLE.OWNER:
      return (
        <UserRoleField>
          You are the <b>Owner</b> of that Tracker.
        </UserRoleField>
      );
    case USER_ROLE.EDITOR:
      return (
        <UserRoleField>
          You are a <b>Editor</b> of that Tracker.
        </UserRoleField>
      );
    case USER_ROLE.BORROWER:
      if (borrowerObject != null) {
        return (
          <UserRoleField>
            You are a <b>Borrower</b> of that Tracker. Your usage time starts at{' '}
            <b>{new Date(borrowerObject.startTime).toLocaleDateString()}</b> and ends at{' '}
            <b>{new Date(borrowerObject.endTime).toLocaleDateString()}</b>.
          </UserRoleField>
        );
      }
      break;
    default:
      if (isAdmin)
        return <UserRoleField>You can see that tracker, because you are an admin!</UserRoleField>;

      return <></>;
  }
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 15px;
  gap: 10px;
`;

const UserRoleField = styled.div`
  margin-bottom: 10px;
`;
